import { Wallet } from '@dynamic-labs/sdk-react-core';
import { isSolanaWallet } from '@dynamic-labs/solana';
import { Rpc } from '@lightprotocol/stateless.js';
import { PublicKey, PublicKeyInitData } from '@solana/web3.js';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { formatUnits } from 'viem';

import { compressedBalanceAtom } from '@claim/atoms';

import { getTotalCompressedBalance } from '../utils/get-total-compressed-balance';
import { useClaim } from './use-claim';

const fetchCompressedTokenBalance = async (
  wallet: Wallet,
  publicKey: PublicKey,
  mintAddress: PublicKey,
  connection: Rpc,
) => {
  if (!wallet?.address) throw new Error('Wallet address is required');

  return await getTotalCompressedBalance(publicKey, mintAddress, connection);
};

export const useCompressedBalance = (wallet: Wallet) => {
  const { mintAddress, connection } = useClaim();
  const setCompressedBalance = useSetAtom(compressedBalanceAtom);

  const publicKey = useMemo(() => {
    if (!wallet || !isSolanaWallet(wallet)) return null;
    return new PublicKey(wallet.address as PublicKeyInitData);
  }, [wallet]);

  return useQuery({
    queryKey: ['compressedTokenBalance', wallet?.address],
    queryFn: async () => {
      if (!publicKey) return;
      const totalCompressedBalance = await fetchCompressedTokenBalance(
        wallet,
        publicKey,
        mintAddress,
        connection,
      );

      if (totalCompressedBalance > 0) {
        setCompressedBalance((prev) => ({
          ...prev,
          [wallet.address]: totalCompressedBalance,
        }));
      }

      return Number(formatUnits(BigInt(totalCompressedBalance), 9));
    },
    enabled: !!wallet?.address && isSolanaWallet(wallet) && !!publicKey,
  });
};
